/**
 * CrawlerSummaryCards.tsx - Crawler performance metrics cards
 *
 * SESSION_400: Displays summary statistics for region crawler performance
 *
 * Design: Industrial/utilitarian aesthetic with clear data hierarchy,
 * monospace numerics, and status-indicating colors.
 */

import React, { useMemo } from 'react';
import {
  SimpleGrid,
  Paper,
  Group,
  Stack,
  Text,
  Box,
  Progress,
  RingProgress,
  Skeleton,
  ThemeIcon
} from '@mantine/core';
import {
  IconRefresh,
  IconClock,
  IconArticle,
  IconMessageCircle,
  IconAt,
  IconStack2,
  IconArrowDown,
  IconTrendingUp,
  IconTrendingDown,
  IconCheck,
  IconX
} from '@tabler/icons-react';
import type { RegionCrawlerStats } from '../../../../domain/entities/Region';

// ============================================================================
// Types
// ============================================================================

interface CrawlerSummaryCardsProps {
  stats: RegionCrawlerStats | null;
  isLoading: boolean;
}

interface MetricCardConfig {
  id: string;
  label: string;
  getValue: (stats: RegionCrawlerStats) => number | string;
  getSubValue?: (stats: RegionCrawlerStats) => string;
  icon: React.ElementType;
  color: string;
  format?: 'number' | 'duration' | 'percent';
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format seconds to human-readable duration
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/**
 * Format large numbers with abbreviations
 */
function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

// ============================================================================
// Metric Configurations
// ============================================================================

const METRIC_CONFIGS: MetricCardConfig[] = [
  {
    id: 'crawls',
    label: 'Crawl Executions',
    getValue: (stats) => stats.totalCrawlsInPeriod,
    getSubValue: (stats) => `${stats.successfulCrawls} succeeded · ${stats.failedCrawls} failed`,
    icon: IconRefresh,
    color: 'blue'
  },
  {
    id: 'duration',
    label: 'Avg Duration',
    getValue: (stats) => stats.averageCrawlDurationSeconds,
    icon: IconClock,
    color: 'cyan',
    format: 'duration'
  },
  {
    id: 'posts',
    label: 'Posts Processed',
    getValue: (stats) => stats.totalPostsProcessed,
    getSubValue: (stats) => `${formatNumber(stats.newContentCount)} new · ${formatNumber(stats.duplicateSkipCount)} duplicates`,
    icon: IconArticle,
    color: 'indigo'
  },
  {
    id: 'comments',
    label: 'Comments Processed',
    getValue: (stats) => stats.totalCommentsProcessed,
    icon: IconMessageCircle,
    color: 'violet'
  },
  {
    id: 'mentions',
    label: 'Mentions Found',
    getValue: (stats) => stats.totalMentionsFound,
    icon: IconAt,
    color: 'teal'
  },
  {
    id: 'queue',
    label: 'Queue Depth',
    getValue: (stats) => stats.unprocessedQueueDepth,
    getSubValue: (stats) => `${stats.postsAwaitingDrilling} posts pending drill`,
    icon: IconStack2,
    color: stats => stats.unprocessedQueueDepth > 1000 ? 'orange' : 'gray'
  }
];

// ============================================================================
// Success Rate Ring Component
// ============================================================================

interface SuccessRateRingProps {
  successful: number;
  failed: number;
  isLoading: boolean;
}

const SuccessRateRing: React.FC<SuccessRateRingProps> = ({
  successful,
  failed,
  isLoading
}): React.ReactElement => {
  const total = successful + failed;
  const successRate = total > 0 ? (successful / total) * 100 : 0;

  const ringColor = useMemo((): string => {
    if (successRate >= 95) return 'teal';
    if (successRate >= 80) return 'green';
    if (successRate >= 60) return 'yellow';
    return 'red';
  }, [successRate]);

  if (isLoading) {
    return <Skeleton height={140} radius="md" />;
  }

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-gray-3)',
        background: 'linear-gradient(135deg, var(--mantine-color-gray-0) 0%, var(--mantine-color-white) 100%)'
      }}
    >
      <Group justify="space-between" align="center">
        <Stack gap={4}>
          <Text
            size="xs"
            fw={600}
            tt="uppercase"
            c="dimmed"
            style={{ letterSpacing: '0.05em' }}
          >
            Success Rate
          </Text>
          <Text
            fw={700}
            style={{
              fontSize: '2rem',
              fontFamily: 'var(--mantine-font-family-monospace)',
              lineHeight: 1
            }}
          >
            {successRate.toFixed(1)}%
          </Text>
          <Group gap="xs" mt={4}>
            <Group gap={4}>
              <IconCheck size={14} color="var(--mantine-color-teal-6)" />
              <Text size="xs" c="dimmed">{successful}</Text>
            </Group>
            <Text size="xs" c="dimmed">·</Text>
            <Group gap={4}>
              <IconX size={14} color="var(--mantine-color-red-6)" />
              <Text size="xs" c="dimmed">{failed}</Text>
            </Group>
          </Group>
        </Stack>

        <RingProgress
          size={80}
          thickness={8}
          roundCaps
          sections={[
            { value: successRate, color: ringColor },
            { value: 100 - successRate, color: 'gray.2' }
          ]}
          label={
            <Box style={{ textAlign: 'center' }}>
              {successRate >= 80 ? (
                <IconTrendingUp size={20} color={`var(--mantine-color-${ringColor}-6)`} />
              ) : (
                <IconTrendingDown size={20} color={`var(--mantine-color-${ringColor}-6)`} />
              )}
            </Box>
          }
        />
      </Group>
    </Paper>
  );
};

// ============================================================================
// Individual Metric Card
// ============================================================================

interface MetricCardProps {
  config: MetricCardConfig;
  stats: RegionCrawlerStats;
  animationDelay: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  config,
  stats,
  animationDelay
}): React.ReactElement => {
  const { label, getValue, getSubValue, icon: IconComponent, color, format } = config;

  const rawValue = getValue(stats);
  const displayValue = useMemo((): string => {
    if (typeof rawValue === 'string') return rawValue;
    if (format === 'duration') return formatDuration(rawValue);
    return formatNumber(rawValue);
  }, [rawValue, format]);

  const subValue = getSubValue ? getSubValue(stats) : null;
  const cardColor = typeof color === 'function' ? color(stats) : color;

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-gray-3)',
        transition: 'all 0.2s ease',
        animationDelay: `${animationDelay}ms`
      }}
      styles={{
        root: {
          '&:hover': {
            borderColor: `var(--mantine-color-${cardColor}-3)`,
            boxShadow: `0 4px 12px var(--mantine-color-${cardColor}-1)`
          }
        }
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text
            size="xs"
            fw={600}
            tt="uppercase"
            c="dimmed"
            style={{ letterSpacing: '0.05em' }}
          >
            {label}
          </Text>
          <Text
            fw={700}
            style={{
              fontSize: '1.75rem',
              fontFamily: 'var(--mantine-font-family-monospace)',
              lineHeight: 1.1,
              color: `var(--mantine-color-${cardColor}-7)`
            }}
          >
            {displayValue}
          </Text>
          {subValue && (
            <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
              {subValue}
            </Text>
          )}
        </Stack>

        <ThemeIcon
          size={40}
          radius="md"
          variant="light"
          color={cardColor}
          style={{
            flexShrink: 0
          }}
        >
          <IconComponent size={22} stroke={1.5} />
        </ThemeIcon>
      </Group>
    </Paper>
  );
};

// ============================================================================
// Queue Health Indicator
// ============================================================================

interface QueueHealthProps {
  queueDepth: number;
  postsAwaitingDrilling: number;
  isLoading: boolean;
}

const QueueHealthIndicator: React.FC<QueueHealthProps> = ({
  queueDepth,
  postsAwaitingDrilling,
  isLoading
}): React.ReactElement => {
  // Calculate health status
  const queueStatus = useMemo((): { color: string; label: string } => {
    if (queueDepth === 0) return { color: 'teal', label: 'Healthy' };
    if (queueDepth < 100) return { color: 'green', label: 'Normal' };
    if (queueDepth < 500) return { color: 'yellow', label: 'Moderate' };
    if (queueDepth < 1000) return { color: 'orange', label: 'High' };
    return { color: 'red', label: 'Critical' };
  }, [queueDepth]);

  const drillingStatus = useMemo((): { color: string; label: string } => {
    if (postsAwaitingDrilling === 0) return { color: 'teal', label: 'Complete' };
    if (postsAwaitingDrilling < 50) return { color: 'green', label: 'Normal' };
    if (postsAwaitingDrilling < 200) return { color: 'yellow', label: 'Backlog' };
    return { color: 'orange', label: 'High Backlog' };
  }, [postsAwaitingDrilling]);

  if (isLoading) {
    return <Skeleton height={120} radius="md" />;
  }

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-gray-3)'
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconStack2 size={18} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={600}>
              Queue Health
            </Text>
          </Group>
          <Text
            size="xs"
            fw={600}
            c={`${queueStatus.color}.7`}
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              backgroundColor: `var(--mantine-color-${queueStatus.color}-1)`
            }}
          >
            {queueStatus.label}
          </Text>
        </Group>

        <Stack gap="xs">
          {/* Match Queue */}
          <Box>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">Match Queue</Text>
              <Text
                size="xs"
                fw={600}
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                {formatNumber(queueDepth)}
              </Text>
            </Group>
            <Progress
              value={Math.min((queueDepth / 1000) * 100, 100)}
              size="sm"
              radius="xs"
              color={queueStatus.color}
            />
          </Box>

          {/* Drilling Backlog */}
          <Box>
            <Group justify="space-between" mb={4}>
              <Group gap={4}>
                <IconArrowDown size={12} color="var(--mantine-color-gray-6)" />
                <Text size="xs" c="dimmed">Drilling Backlog</Text>
              </Group>
              <Text
                size="xs"
                fw={600}
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                {formatNumber(postsAwaitingDrilling)}
              </Text>
            </Group>
            <Progress
              value={Math.min((postsAwaitingDrilling / 500) * 100, 100)}
              size="sm"
              radius="xs"
              color={drillingStatus.color}
            />
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CrawlerSummaryCards: React.FC<CrawlerSummaryCardsProps> = ({
  stats,
  isLoading
}): React.ReactElement => {
  if (isLoading || !stats) {
    return (
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height={100} radius="md" />
          ))}
        </SimpleGrid>
      </Stack>
    );
  }

  // Filter out queue-related metrics as they have dedicated component
  const mainMetrics = METRIC_CONFIGS.filter(c => c.id !== 'queue');

  return (
    <Stack gap="lg">
      {/* Success Rate + Queue Health Row */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <SuccessRateRing
          successful={stats.successfulCrawls}
          failed={stats.failedCrawls}
          isLoading={isLoading}
        />
        <QueueHealthIndicator
          queueDepth={stats.unprocessedQueueDepth}
          postsAwaitingDrilling={stats.postsAwaitingDrilling}
          isLoading={isLoading}
        />
      </SimpleGrid>

      {/* Main Metrics Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {mainMetrics.map((config, index) => (
          <MetricCard
            key={config.id}
            config={config}
            stats={stats}
            animationDelay={index * 50}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

export default CrawlerSummaryCards;
